import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TwitterIcon from '@mui/icons-material/Twitter';
import ekstraklasaImage from '../assets/images/ekstraklasa.webp';
import liga1Image from '../assets/images/1liga.webp';
import liga2Image from '../assets/images/2liga.webp';
import lowerLeaguesImage from '../assets/images/niskie.webp';
import worldcupImage from '../assets/images/worldcup.webp';
import SupportBox from './SupportBox';
import { fetchArticles, formatArticleDate } from '../services/strapiBlog';

const quickAccessTiles = [
  { title: 'Ekstraklasa', description: 'Symulacje Ekstraklasy!', link: '/liga/432/ekstraklasa', image: ekstraklasaImage },
  { title: '1. Liga', description: 'Symulacje 1. ligi!', link: '/liga/433/1-liga', image: liga1Image },
  { title: '2. Liga', description: 'Symulacje 2. ligi', link: '/liga/434/2-liga', image: liga2Image },
  { title: 'Niższe ligi', description: 'Wybierz z górnego menu (Rozgrywki)', image: lowerLeaguesImage },
];

const defaultArticleImage = '/images/symuliga.webp';

const HomePage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError(null);

    fetchArticles({ page: 1, pageSize: 3 })
      .then((result) => {
        if (!active) return;
        setArticles(result.articles);
      })
      .catch((requestError) => {
        if (!active) return;
        console.error('Strapi latest articles request failed:', requestError);
        setError('Nie udało się pobrać artykułów.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <Box>
      <Box sx={{ textAlign: 'center', padding: { xs: '20px', md: '40px' } }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          SymuLiga.pl - centrum predykcji polskiej piłki!
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, maxWidth: '650px', margin: '0 auto' }}>
          Symulujemy wyniki wszystkich lig w Polsce. Sprawdź gdzie skończy Twój klub!
          Chcesz współpracować lub masz pytania? Pisz na kontakt@symuliga.pl.
        </Typography>
        <Button
          component="a"
          href="https://x.com/symuligaPL"
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<TwitterIcon sx={{ fontSize: 16 }} />}
          size="small"
          variant="text"
          sx={{ color: '#1da1f2', fontSize: '0.875rem', mb: 0 }}
        >
          Odwiedź nas na X
        </Button>
      </Box>

      <Box sx={{ padding: { xs: '20px', md: '40px' } }}>
        <Grid container spacing={3} justifyContent="center">
          {quickAccessTiles.map((tile) => (
            <Grid item xs={12} sm={6} md={4} key={tile.title}>
              {tile.link ? (
                <Link to={tile.link} style={{ textDecoration: 'none' }}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: tile.featured ? '2px solid #FFD700' : 'none',
                      transform: tile.featured ? 'scale(1.03)' : 'none',
                      boxShadow: tile.featured ? 6 : 1,
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={tile.image}
                      alt={tile.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography variant="h6">{tile.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tile.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Link>
              ) : (
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={tile.image}
                    alt={tile.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="h6">{tile.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tile.description}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Grid>
          ))}
        </Grid>
      </Box>

      <SupportBox />

      <Box sx={{ padding: { xs: '20px', md: '40px' } }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Najnowsze artykuły
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress color="warning" />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : articles.length === 0 ? (
          <Alert severity="info">Brak artykułów.</Alert>
        ) : (
          <Grid container spacing={3} justifyContent="center">
            {articles.map((article) => (
              <Grid item xs={12} sm={6} md={4} key={article.id}>
                <Card
                  sx={{
                    height: '100%',
                    backgroundColor: '#2c2c2c',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                  }}
                >
                  <CardActionArea component={Link} to={`/blog/${article.slug}`} sx={{ height: '100%' }}>
                    <CardMedia
                      component="img"
                      height="178"
                      image={article.cover?.url || defaultArticleImage}
                      alt={article.cover?.alternativeText || article.title}
                      sx={{ objectFit: 'cover', backgroundColor: '#1a1a1a' }}
                    />
                    <CardContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 210 }}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                        {article.category?.name && <Chip label={article.category.name} size="small" />}
                        
                        {article.publishedAt && (
                          <Chip label={formatArticleDate(article.publishedAt)} size="small" variant="outlined" />
                        )}
                      </Stack>
                      <Typography variant="h6" sx={{ lineHeight: 1.25, mb: 1 }}>
                        {article.title}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', mb: 2, flexGrow: 1 }}>
                        {article.description}
                      </Typography>
                      <Button
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ alignSelf: 'flex-start', color: '#FFD700' }}
                      >
                        Czytaj
                      </Button>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default HomePage;
